import { useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError } from './api.js';
import type { ClienteHttp } from './api.js';

/**
 * Cadastro de empresa emissora.
 *
 * Mora no pacote compartilhado porque o backoffice usa a mesma tela quando a
 * operacao faz o onboarding pelo cliente. Duas copias de um formulario de
 * duzentas linhas divergem na primeira correcao que so uma delas recebe.
 *
 * Até aqui o único caminho era chamar a API à mão, o que deixava todo
 * onboarding dependente do suporte. O formulário pede exatamente o que a
 * SEFAZ exige no grupo `emit` da nota — campo a mais em tela de cadastro é
 * campo que alguém preenche errado.
 *
 * O ambiente começa em homologação de propósito: emitir em produção sem ter
 * visto uma nota autorizada em teste é a forma mais cara de descobrir um
 * cadastro errado, porque a numeração é consumida e a correção vira
 * cancelamento.
 */

const CRTS = [
  { id: '1', label: 'Simples Nacional' },
  { id: '2', label: 'Simples Nacional — excesso de sublimite' },
  { id: '3', label: 'Regime normal' },
  { id: '4', label: 'MEI' },
] as const;

const UFS = [
  'AC',
  'AL',
  'AM',
  'AP',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MG',
  'MS',
  'MT',
  'PA',
  'PB',
  'PE',
  'PI',
  'PR',
  'RJ',
  'RN',
  'RO',
  'RR',
  'RS',
  'SC',
  'SE',
  'SP',
  'TO',
] as const;

type Campos = Record<string, string>;

const VAZIO: Campos = {
  cnpj: '',
  razao_social: '',
  nome_fantasia: '',
  ie: '',
  im: '',
  crt: '3',
  uf: 'MS',
  municipio: '',
  codigo_municipio: '',
  logradouro: '',
  numero: '',
  complemento: '',
  bairro: '',
  cep: '',
  telefone: '',
  environment: 'sandbox',
  csc_id: '',
  csc: '',
};

export function EmitterForm({
  http,
  iniciais,
  onFechar,
}: {
  http: ClienteHttp;
  /** Pre-preenchimento vindo da importacao de PDF. */
  iniciais?: Record<string, string>;
  onFechar: () => void;
}): ReactNode {
  const queryClient = useQueryClient();
  const [campos, setCampos] = useState<Campos>({ ...VAZIO, ...iniciais });
  const [erro, setErro] = useState<string | null>(null);
  const [porCampo, setPorCampo] = useState<Record<string, string>>({});

  const criar = useMutation({
    mutationFn: (dados: Campos) => {
      // Opcional vazio não é enviado: string vazia reprovaria na validação de
      // tamanho mínimo da API e o erro apareceria num campo que o usuário
      // deixou em branco de propósito.
      const corpo: Record<string, string> = {};
      for (const [chave, valor] of Object.entries(dados)) {
        if (valor.trim()) corpo[chave] = valor.trim();
      }
      return http.api<{ id: string }>('/v1/emitters', { method: 'POST', body: corpo });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['emitters'] });
      onFechar();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setErro(err.message);
        setPorCampo(err.fieldErrors);
        return;
      }
      setErro('Não foi possível cadastrar a empresa.');
    },
  });

  const campo = (
    nome: string,
    rotulo: string,
    extra: { required?: boolean; placeholder?: string; dica?: string } = {},
  ): ReactNode => (
    <label className="campo" key={nome}>
      {rotulo}
      <input
        className="input"
        required={extra.required ?? false}
        placeholder={extra.placeholder ?? ''}
        value={campos[nome] ?? ''}
        onChange={(e) => setCampos({ ...campos, [nome]: e.target.value })}
      />
      {porCampo[nome] ? (
        <small className="campo-erro">{porCampo[nome]}</small>
      ) : extra.dica ? (
        <small className="campo-dica">{extra.dica}</small>
      ) : null}
    </label>
  );

  function enviar(e: FormEvent): void {
    e.preventDefault();
    setErro(null);
    setPorCampo({});
    criar.mutate(campos);
  }

  return (
    <form className="card form-card" onSubmit={enviar}>
      <h3 style={{ margin: '0 0 4px' }}>Nova empresa emissora</h3>
      <p className="sub" style={{ margin: '0 0 16px' }}>
        Os dados precisam bater com o cadastro na SEFAZ: razão social, endereço e inscrição estadual entram na
        nota exatamente como aqui.
      </p>

      {erro && (
        <div className="alert bad" style={{ marginBottom: 14 }}>
          <b>Não deu para cadastrar</b>
          {erro}
        </div>
      )}

      <div className="grid-campos">
        {campo('cnpj', 'CNPJ', { required: true, placeholder: '00.000.000/0000-00' })}
        {campo('razao_social', 'Razão social', { required: true })}
        {campo('nome_fantasia', 'Nome fantasia')}
        {campo('ie', 'Inscrição estadual', { required: true, dica: 'ISENTO quando não houver' })}
        {campo('im', 'Inscrição municipal', { dica: 'Obrigatória para NFS-e' })}

        <label className="campo">
          Regime tributário
          <select
            className="input"
            value={campos.crt}
            onChange={(e) => setCampos({ ...campos, crt: e.target.value })}
          >
            {CRTS.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
        </label>

        <label className="campo">
          UF
          <select
            className="input"
            value={campos.uf}
            onChange={(e) => setCampos({ ...campos, uf: e.target.value })}
          >
            {UFS.map((uf) => (
              <option key={uf} value={uf}>
                {uf}
              </option>
            ))}
          </select>
        </label>

        {campo('municipio', 'Município', { required: true })}
        {campo('codigo_municipio', 'Código IBGE do município', {
          required: true,
          placeholder: '5002704',
          dica: '7 dígitos, na tabela do IBGE',
        })}
        {campo('cep', 'CEP', { required: true, placeholder: '00000-000' })}
        {campo('logradouro', 'Logradouro', { required: true })}
        {campo('numero', 'Número', { required: true })}
        {campo('complemento', 'Complemento')}
        {campo('bairro', 'Bairro', { required: true })}
        {campo('telefone', 'Telefone')}

        <label className="campo">
          Ambiente
          <select
            className="input"
            value={campos.environment}
            onChange={(e) => setCampos({ ...campos, environment: e.target.value })}
          >
            <option value="sandbox">Homologação (teste)</option>
            <option value="production">Produção (nota com valor fiscal)</option>
          </select>
          <small className="campo-dica">Comece em homologação e troque depois do primeiro teste.</small>
        </label>

        {campo('csc_id', 'ID do CSC', { placeholder: '000001', dica: 'Só para NFC-e' })}
        {campo('csc', 'CSC (código de segurança)', { dica: 'Obtido no portal da SEFAZ da UF' })}
      </div>

      <div className="fix" style={{ marginTop: 16 }}>
        <button className="btn btn-primary" type="submit" disabled={criar.isPending}>
          {criar.isPending ? 'Cadastrando...' : 'Cadastrar empresa'}
        </button>{' '}
        <button className="btn btn-ghost" type="button" onClick={onFechar}>
          Cancelar
        </button>
      </div>
    </form>
  );
}
